import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIcapsLogout } from './icaps/useIcapsLogout';
import { postUserReplacement } from '../network/endpoints/cart/postUserReplacement';
import { IdentityContext } from '../providers/IdentityProvider';
import { AuthEvent } from './icaps/useIcaps';
import { logoutFromAuth0 } from './useAuth0';
import { isCiamEnabled } from '../utils/retailUnitsWithCiam';
import { StorageState } from '../actions/enums';

const Actions = {
  LOG_OUT_BUSINESS_CUSTOMER: 'user/logoutBusinessCustomer',
  LOG_OUT_BUSINESS_CUSTOMER_SUCCESS: 'user/logoutBusinessCustomerSuccess',
  LOG_OUT_BUSINESS_CUSTOMER_FAIL: 'user/logoutBusinessCustomerFail',
} as const;

type Actions = (typeof Actions)[keyof typeof Actions];

interface MoveBusinessCartItemsPayload {
  userId: string;
  provider: 'IRW' | 'Ciam';
}

function onLogoutFail({ status }: AuthEvent) {
  if (status === 'log-out-attempt-failure') {
    globalThis.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
  }
}

export const useLogoutBusinessCustomer = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isReplaced = useRef(false);
  const { getSession } = useContext(IdentityContext);
  const icapsLogout = useIcapsLogout(onLogoutFail);

  const pubSubLoaded = useCallback(() => {
    if (globalThis?.ikea?.pubsub) {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (document.readyState === 'complete') {
      pubSubLoaded();
    } else {
      globalThis.addEventListener('load', pubSubLoaded);
      return () => {
        globalThis.removeEventListener('load', pubSubLoaded);
      };
    }
  }, [pubSubLoaded]);

  const moveBusinessCartItemsCallback = useCallback(
    async (payload: MoveBusinessCartItemsPayload) => {
      try {
        await getSession(true);
        await postUserReplacement(payload.userId);
        localStorage.removeItem(StorageState.BUSINESS_CUSTOMER_USER_REPLACEMENT);
        globalThis.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_SUCCESS);
      } catch {
        localStorage.removeItem(StorageState.BUSINESS_CUSTOMER_USER_REPLACEMENT);
        globalThis.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
      }
    },
    [getSession],
  );

  const logoutBusinessCustomerCallback = useCallback(
    (payload: MoveBusinessCartItemsPayload) => {
      try {
        if (isCiamEnabled) {
          // CIAM flow: Save payload to localStorage, then redirect via Auth0 logout.
          // On return, the stored payload is detected and moveBusinessCartItemsCallback runs directly.
          localStorage.setItem(StorageState.BUSINESS_CUSTOMER_USER_REPLACEMENT, JSON.stringify(payload));
          logoutFromAuth0();
        } else {
          // ICAPS flow: Keep existing synchronous behavior
          icapsLogout(() => moveBusinessCartItemsCallback(payload));
        }
      } catch {
        globalThis.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
      }
    },
    [icapsLogout, moveBusinessCartItemsCallback],
  );

  // CIAM post-redirect: if we returned from Okta logout with a stored payload, run replacement directly.
  // The payload is only removed once the replacement succeeds or fails (inside moveBusinessCartItemsCallback).
  useEffect(() => {
    if (!isLoaded) return;
    const stored = localStorage.getItem(StorageState.BUSINESS_CUSTOMER_USER_REPLACEMENT);
    if (!stored) return;
    if (isReplaced.current) return;
    isReplaced.current = true;
    moveBusinessCartItemsCallback(JSON.parse(stored) as MoveBusinessCartItemsPayload);
  }, [isLoaded, moveBusinessCartItemsCallback]);

  useEffect(() => {
    if (isLoaded) {
      globalThis.ikea.pubsub.subscribe(Actions.LOG_OUT_BUSINESS_CUSTOMER, logoutBusinessCustomerCallback);
    }
    return () => {
      globalThis.ikea?.pubsub?.unsubscribe(Actions.LOG_OUT_BUSINESS_CUSTOMER, logoutBusinessCustomerCallback);
    };
  }, [isLoaded, logoutBusinessCustomerCallback]);
};
